import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { EDITION_RANK, type EditionKey } from '@/lib/capabilities/contracts';
import { buildCatalogHierarchy } from '@/lib/capabilities/catalogHierarchy';
import type { AnyCatalogNode } from '@/lib/capabilities/contracts';
import { CATALOG } from '@/lib/capabilities/catalog';
import { Info } from './Info';
import { MATRIX_DESCRIPTIONS } from './matrixDescriptions';
import { HowItWorks, RowTypesHelp } from './Help';
import CreditPolicy from './CreditPolicy';
import {
  listEditions,
  getEditionMatrix,
  getAddonMatrix,
  getAddonSwitches,
  getAddonPlanImpact,
  setAddonPlanState,
  type AddonPlanState,
  globalHolds,
  setEditionGrant,
  type Edition,
  type GlobalHold,
} from './api';

/** DB plan_key → commercial edition rank, for the minEdition default. */
// Developer (internal, tracker 47) sits above every floor: it has every feature, so no floor ever excludes it.
const PLAN_RANK: Record<string, number> = { developer: 99, free: 0, starter: 1, pro: 2, professional: 2, enterprise: 3 };

/** One-line description shown when hovering an edition column header. */
const EDITION_BLURB: Record<string, string> = {
  developer:
    'Internal, not sold: your own test workspaces. Every feature is on here, including ones no plan has yet. A new switch ships on Developer only; release it by ticking the plans you sell it on. Add-ons are Included or Off here, never for sale.',
  free: 'Entry level. The core basics to get started, at no cost.',
  starter: 'Small teams. Adds everyday productivity features on top of Free.',
  pro: 'Growing organisations. Automation, scheduling, contractors and AI.',
  professional: 'Growing organisations. Automation, scheduling, contractors and AI.',
  enterprise: 'Large organisations. Analytics, integrations, developer APIs and administration.',
};

function gkey(plan: string, cap: string) {
  return `${plan}|${cap}`;
}

/**
 * Every row that switches off when this one does: the catalogue's dependants,
 * all the way down (the app's resolver and the server walk the same chain).
 * Shown under each row so a switch never hides what else it takes with it.
 */
const DIRECT_DEPENDANTS: Map<string, string[]> = (() => {
  const map = new Map<string, string[]>();
  for (const node of Object.values(CATALOG) as AnyCatalogNode[]) {
    for (const dep of node.dependsOn) {
      const list = map.get(dep) ?? [];
      list.push(node.key);
      map.set(dep, list);
    }
  }
  return map;
})();

export function switchesOffWith(key: string): string[] {
  const seen = new Set<string>();
  const queue = [...(DIRECT_DEPENDANTS.get(key) ?? [])];
  while (queue.length) {
    const next = queue.shift()!;
    if (seen.has(next)) continue;
    const node = (CATALOG as Record<string, AnyCatalogNode>)[next];
    // An Always included row never switches off, so nothing goes "with" it.
    if (!node || node.alwaysIncluded) continue;
    seen.add(next);
    queue.push(...(DIRECT_DEPENDANTS.get(next) ?? []));
  }
  return [...seen]
    .map((k) => (CATALOG as Record<string, AnyCatalogNode>)[k]?.title ?? k)
    .sort((a, b) => a.localeCompare(b));
}

/** The plain-English lines under a row (Jay, 2026-11-01): what it does, what unticking it loses. */
function RowDescription({ node }: { node: AnyCatalogNode }) {
  const words = MATRIX_DESCRIPTIONS[node.key];
  const alsoOff = node.alwaysIncluded ? [] : switchesOffWith(node.key);
  if (!words && alsoOff.length === 0) return null;
  return (
    <div className="feat-desc">
      {words ? <div>{words.does}</div> : null}
      {words ? (
        <div>
          <strong>{node.alwaysIncluded ? 'Cannot be switched off.' : 'When unticked:'}</strong>{' '}
          {node.alwaysIncluded ? words.off.replace(/^Always included:\s*/i, '') : words.off}
        </div>
      ) : null}
      {alsoOff.length > 0 ? (
        <div className="feat-also">
          <strong>Also switches off ({alsoOff.length}):</strong> {alsoOff.join(', ')}
        </div>
      ) : null}
    </div>
  );
}

export default function Matrix({ toast }: { toast: (m: string) => void }) {
  const groups = useMemo(() => buildCatalogHierarchy(), []);
  const [editions, setEditions] = useState<Edition[]>([]);
  const [grants, setGrants] = useState<Map<string, 'enable' | 'disable'>>(new Map());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  // Held back for every workspace: a plan tick cannot release these (a global disable is absolute).
  const [holds, setHolds] = useState<Map<string, GlobalHold>>(new Map());
  // Tracker 44: switches an add-on turns on -> plans that can buy it. A cell
  // that is off for the plan but sold to it as an add-on shows "Add-on".
  const [addonPlans, setAddonPlans] = useState<Record<string, string[]>>({});
  // Switch -> the add-on that sells it. Those rows are Off / Add-on / Included.
  const [addonOf, setAddonOf] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    const [eds, matrix, held, addons, addonKeys] = await Promise.all([
      listEditions(),
      getEditionMatrix(),
      globalHolds().catch(() => [] as GlobalHold[]),
      getAddonMatrix().catch(() => ({}) as Record<string, string[]>),
      getAddonSwitches().catch(() => ({}) as Record<string, string>),
    ]);
    setAddonOf(addonKeys);
    setEditions(eds);
    setAddonPlans(addons);
    setHolds(new Map(held.map((h) => [h.capability_key, h])));
    setGrants(new Map(matrix.map((g) => [gkey(g.plan_key, g.capability_key), g.effect])));
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // A plain cell shows what the app RESOLVES, not what the edition floor would
  // suggest: the resolver enables every active, non-beta node unless a grant row
  // disables it (`resolveCapabilities`), so a cell drawn from `minEdition` alone
  // told admins a feature was excluded from Free while every Free workspace had
  // it. The floor is kept as a hint and can be materialised with one click.
  const floorExcludes = useCallback(
    (plan: string, node: AnyCatalogNode) =>
      (PLAN_RANK[plan] ?? 0) < EDITION_RANK[node.minEdition as EditionKey],
    []
  );
  const isIncluded = useCallback(
    (plan: string, node: AnyCatalogNode) => {
      const explicit = grants.get(gkey(plan, node.key));
      if (explicit) return { included: explicit === 'enable', explicit: true, floorExcludes: floorExcludes(plan, node) };
      return { included: node.active && !node.betaFlag, explicit: false, floorExcludes: floorExcludes(plan, node) };
    },
    [floorExcludes, grants]
  );

  /** Writes a DISABLE row for every plain cell whose edition floor sits above the plan. */
  const applyEditionFloors = useCallback(async () => {
    const pending: Array<{ plan: string; key: string }> = [];
    for (const ed of editions) {
      for (const group of groups) {
        for (const node of group.features) {
          if (!node.alwaysIncluded && !grants.has(gkey(ed.plan_key, node.key)) && floorExcludes(ed.plan_key, node)) {
            pending.push({ plan: ed.plan_key, key: node.key });
          }
        }
      }
    }
    if (pending.length === 0) {
      toast('Every edition floor is already recorded.');
      return;
    }
    if (!window.confirm(`Exclude ${pending.length} feature/edition pairs that sit below their minimum edition?`)) return;
    try {
      for (const p of pending) await setEditionGrant(p.plan, p.key, false);
      toast(`Recorded ${pending.length} exclusions.`);
    } catch (e) {
      toast(`Failed: ${(e as Error).message}`);
    }
    void load();
  }, [editions, floorExcludes, grants, groups, load, toast]);

  const toggleCell = useCallback(
    async (plan: string, node: AnyCatalogNode, currentlyIncluded: boolean) => {
      const next = !currentlyIncluded;
      // optimistic
      setGrants((prev) => {
        const m = new Map(prev);
        m.set(gkey(plan, node.key), next ? 'enable' : 'disable');
        return m;
      });
      try {
        await setEditionGrant(plan, node.key, next);
      } catch (e) {
        toast(`Failed: ${(e as Error).message}`);
        void load();
      }
    },
    [load, toast]
  );

  /**
   * An add-on row (Jay, 2 Nov): Off, Add-on (bought per asset) or Included,
   * per plan. Asks first when it ends trials or paid add-ons on that plan.
   */
  const setAddonState = useCallback(
    async (plan: string, addonKey: string, state: AddonPlanState) => {
      try {
        if (state !== 'addon') {
          const affected = await getAddonPlanImpact(addonKey, plan);
          if (
            affected > 0 &&
            !window.confirm(
              state === 'included'
                ? `${affected} ${plan} workspace(s) have this add-on on now. Including it in ${plan} stops their per-asset charge; they keep the feature. Continue?`
                : `${affected} ${plan} workspace(s) have this add-on on now. Turning it off for ${plan} ends it for them and they lose the feature. Continue?`
            )
          )
            return;
        }
        const ended = await setAddonPlanState(addonKey, plan, state);
        toast(
          `${plan}: ${state === 'addon' ? 'Add-on (paid per asset)' : state === 'included' ? 'Included' : 'Off'}` +
            (ended > 0 ? `. Ended the add-on for ${ended} workspace(s).` : '.')
        );
      } catch (e) {
        toast(`Failed: ${(e as Error).message}`);
      }
      void load();
    },
    [load, toast]
  );

  const q = query.trim().toLowerCase();
  const matchesQuery = (n: AnyCatalogNode) =>
    q.length === 0 ||
    [n.title, n.key, n.description ?? '', MATRIX_DESCRIPTIONS[n.key]?.does ?? '', MATRIX_DESCRIPTIONS[n.key]?.off ?? '', ...(n.tags ?? [])].some((s) => s.toLowerCase().includes(q));

  if (loading) return <p className="muted">Loading matrix…</p>;
  if (editions.length === 0)
    return <p className="muted">No editions found (need platform-admin access, and the free / starter / pro / enterprise editions).</p>;

  const Cell = ({ plan, node }: { plan: string; node: AnyCatalogNode }) => {
    // Always included (tracker 34.05): locked on, no toggle. The app and the
    // server ignore any grant for it, so a click here could only mislead.
    if (node.alwaysIncluded) {
      return (
        <span className="cell on locked" title={`Always included in every plan: ${node.alwaysIncluded}`} aria-label="Always included">
          🔒
        </span>
      );
    }
    const { included, explicit, floorExcludes: belowFloor } = isIncluded(plan, node);
    const held = holds.get(node.key);
    const addonKey = addonOf[node.key];
    if (addonKey) {
      const state: AddonPlanState = included ? 'included' : (addonPlans[node.key] ?? []).includes(plan) ? 'addon' : 'off';
      const colours: Record<AddonPlanState, { background: string; color: string }> = {
        off: { background: '#f1f5f9', color: '#64748b' },
        addon: { background: '#fef3c7', color: '#92400e' },
        included: { background: '#dcfce7', color: '#166534' },
      };
      return (
        <select
          className="cell addon"
          aria-label={`${node.title} on ${plan}`}
          title={`Off: ${plan} cannot have it. Add-on: ${plan} buys it per asset a month (price, trial, annual months and what it counts are on Pricing > a version > Add-ons). Included: ${plan} has it at no extra charge.`}
          value={state}
          style={{ fontSize: 11, fontWeight: 700, padding: '2px 4px', borderRadius: 4, border: 'none', ...colours[state] }}
          onChange={(e) => void setAddonState(plan, addonKey, e.target.value as AddonPlanState)}
        >
          <option value="off">Off</option>
          {/* Developer (internal) never sells an add-on: Included or Off only. */}
          {editions.find((e) => e.plan_key === plan)?.is_internal ? null : <option value="addon">Add-on</option>}
          <option value="included">Included</option>
        </select>
      );
    }
    const how = explicit
      ? 'explicitly set'
      : `default: nothing excludes it${belowFloor ? `; the ${node.minEdition} floor suggests excluding it` : ''}`;
    return (
      <span
        className={`cell ${included ? 'on' : 'off'} ${explicit ? 'explicit' : ''} ${!explicit && belowFloor ? 'below-floor' : ''} ${held ? 'held' : ''}`}
        title={
          held
            ? `Held back for every workspace (${held.reason ?? 'global disable'}). This plan ${included ? 'includes' : 'excludes'} it once released - click to toggle`
            : `${included ? 'Included' : 'Excluded'} in ${plan} (${how}) - click to toggle`
        }
        onClick={() => void toggleCell(plan, node, included)}
      >
        {included ? '✓' : '·'}
      </span>
    );
  };

  const FeatureRow = ({ node, indent }: { node: AnyCatalogNode; indent: boolean }) => (
    <tr>
      <td style={{ paddingLeft: indent ? 28 : 10 }}>
        <div className="feat-name">
          {node.title}{' '}
          {node.betaFlag ? <span className="pill beta">BETA</span> : null}
          {holds.has(node.key) ? (
            <span className="pill held" title={holds.get(node.key)!.reason ?? 'Held back for every workspace'}>
              Held back for everyone
            </span>
          ) : null}
          {node.alwaysIncluded ? (
            <span className="pill" title={node.alwaysIncluded}>
              Always included
            </span>
          ) : null}
          {node.layer === 'journey' ? <span className="pill">journey</span> : null}
          {node.layer === 'platform_service' ? <span className="pill">service</span> : null}
        </div>
        <RowDescription node={node} />
        <div className="feat-key">{node.key}</div>
      </td>
      {editions.map((ed) => (
        <td key={ed.plan_key} className="col-ed">
          <Cell plan={ed.plan_key} node={node} />
        </td>
      ))}
    </tr>
  );

  return (
    <div>
      <CreditPolicy toast={toast} />
      <div className="card" style={{ marginBottom: 16 }}>
        <strong>Maturity settings are not an active runtime control.</strong>
        <p className="sub" style={{ marginBottom: 0 }}>
          The previous Level selector wrote database values that the application did not consume.
          It has been removed from this active control surface until a safe runtime contract exists.
        </p>
      </div>
      {holds.size > 0 ? (
        <div className="card" style={{ marginBottom: 16 }}>
          <strong>Held back for every workspace, whatever the plan</strong>
          <p className="sub" style={{ marginBottom: 8 }}>
            A global hold wins over every tick below: switching one of these on for a plan changes nothing until the hold is
            deleted. Ticks still decide which plans get it once it is released.
          </p>
          <ul style={{ margin: 0 }}>
            {[...holds.values()].map((h) => (
              <li key={h.capability_key}>
                <code>{h.capability_key}</code>
                {h.reason ? <span className="muted"> - {h.reason}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <HowItWorks />
      <div className="row" style={{ marginBottom: 12 }}>
        <input
          placeholder="Search modules / features…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <button className="btn" onClick={() => void load()}>
          Refresh
        </button>
        <button className="btn" onClick={() => void applyEditionFloors()} title="Record a DISABLE row for every plain cell below its feature's minimum edition">
          Apply edition floors
        </button>
      </div>
      <div className="legend">
        <span><span className="cell on" style={{ width: 18, height: 18 }}>✓</span> included</span>
        <span><span className="cell off" style={{ width: 18, height: 18 }}>·</span> excluded</span>
        <span><span className="cell on explicit" style={{ width: 18, height: 18 }}>✓</span> explicitly set (amber ring)</span>
        <span><span className="cell on below-floor" style={{ width: 18, height: 18 }}>✓</span> included by default although the edition floor suggests excluding it (use Apply edition floors)</span>
        <span>a plain cell shows what workspaces on that edition actually get: everything, until a row excludes it</span>
      </div>
      <div className="matrix-scroll">
        <table className="matrix">
          <thead>
            <tr>
              <th>
                Module / Feature
                <Info title="Rows: modules &amp; features"><RowTypesHelp /></Info>
              </th>
              {editions.map((ed) => (
                <th key={ed.plan_key} className="col-ed">
                  {ed.display_name}
                  {ed.is_internal ? (
                    <>
                      <br />
                      <span className="pill" title="Admin only: never on the pricing page, the store or any plan picker">internal, not sold</span>
                    </>
                  ) : null}
                  <Info title={`${ed.display_name} edition`}>
                    <p>{EDITION_BLURB[ed.plan_key] ?? 'A commercial plan. Tick a cell to include a feature for every workspace on this edition.'}</p>
                  </Info>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              // A search opens every group it finds something in (sj-09), so
              // "sub" shows the Sub-jobs row even when Jobs was folded.
              const isCollapsed = q.length === 0 && collapsed.has(group.key);
              const features = group.features.filter(matchesQuery);
              const moduleMatches = group.module ? matchesQuery(group.module) : false;
              if (features.length === 0 && !moduleMatches && q.length > 0) return null;
              return (
                <React.Fragment key={group.key}>
                  <tr
                    className="grp-row"
                    onClick={() =>
                      setCollapsed((prev) => {
                        const s = new Set(prev);
                        s.has(group.key) ? s.delete(group.key) : s.add(group.key);
                        return s;
                      })
                    }
                  >
                    <td>
                      <span className="caret">{isCollapsed ? '▸' : '▾'}</span>
                      {group.title}
                      <span className="muted" style={{ fontWeight: 400 }}> · {features.length}</span>
                      {group.module ? <RowDescription node={group.module} /> : null}
                    </td>
                    {editions.map((ed) => (
                      <td key={ed.plan_key} className="col-ed">
                        {group.module ? <Cell plan={ed.plan_key} node={group.module} /> : null}
                      </td>
                    ))}
                  </tr>
                  {!isCollapsed &&
                    features.map((node) => <FeatureRow key={node.key} node={node} indent />)}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
